import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { supabase, isManager } from './supabase';
import { useNavigate } from 'react-router-dom';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const ENTITY_COLORS = {
  national:     '#EF4444',
  florida:      '#3B82F6',
  newjersey:    '#22C55E',
  academy:      '#F59E0B',
  technologies: '#8B5CF6',
};

const ENTITY_LABELS = {
  national:     'REDLINE National',
  florida:      'Fire Watch Tampa Bay',
  newjersey:    'Fire Watch NJ',
  academy:      'Fire Watch Academy',
  technologies: 'Fire Watch Technologies',
};

function makePin(color) {
  return L.divIcon({
    className: '',
    html: `<div style="width:12px;height:12px;border-radius:50%;background:${color};border:2.5px solid #fff;box-shadow:0 1px 5px rgba(0,0,0,0.35);"></div>`,
    iconSize: [12, 12],
    iconAnchor: [6, 6],
    popupAnchor: [0, -10],
  });
}

function MapFitter({ markers }) {
  const map = useMap();
  useEffect(() => {
    const coords = markers.filter(m => m.lat && m.lng).map(m => [m.lat, m.lng]);
    if (coords.length === 0) return;
    if (coords.length === 1) { map.setView(coords[0], 13); return; }
    map.fitBounds(coords, { padding: [50, 50] });
  }, [markers.length]);
  return null;
}

export default function PropertiesPage({ profile }) {
  const navigate = useNavigate();
  const manager = isManager(profile);
  const [properties, setProperties] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [dispatches, setDispatches] = useState([]);
  const [markers, setMarkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterEntity, setFilterEntity] = useState('');
  const [filterState, setFilterState] = useState('');
  const [search, setSearch] = useState('');
  const [expandedEntity, setExpandedEntity] = useState(null);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    const [{ data: props }, { data: conts }, { data: disps }] = await Promise.all([
      supabase.from('properties').select('*, companies(name)').order('name'),
      supabase.from('contacts').select('id, full_name, email, phone, title, contact_type, property_id, is_billing_contact').not('property_id', 'is', null),
      supabase.from('dispatches').select('id, property_id, status').not('property_id', 'is', null),
    ]);
    const allProps = props || [];
    setProperties(allProps);
    setContacts(conts || []);
    setDispatches(disps || []);
    setMarkers(allProps.filter(p => p.lat && p.lng));
    setLoading(false);
  }

  function activeJobs(propId) {
    return dispatches.filter(d => d.property_id === propId && (d.status === 'Active' || d.status === 'Dispatched')).length;
  }

  function propContacts(propId) {
    return contacts.filter(c => c.property_id === propId);
  }

  const filtered = properties.filter(p => {
    if (filterEntity && p.assigned_entity !== filterEntity) return false;
    if (filterState && p.state !== filterState) return false;
    if (search) {
      const q = search.toLowerCase();
      return (p.name || '').toLowerCase().includes(q) ||
        (p.site_address || '').toLowerCase().includes(q) ||
        (p.city || '').toLowerCase().includes(q) ||
        (p.companies?.name || '').toLowerCase().includes(q);
    }
    return true;
  });

  const filteredMarkers = markers.filter(m => filtered.find(p => p.id === m.id));

  const grouped = {};
  filtered.forEach(p => {
    const e = p.assigned_entity || 'unassigned';
    if (!grouped[e]) grouped[e] = [];
    grouped[e].push(p);
  });

  const allStates = [...new Set(properties.map(p => p.state).filter(Boolean))].sort();
  const totalActive = dispatches.filter(d => d.status === 'Active' || d.status === 'Dispatched').length;

  return (
    <div>
      {/* Stats */}
      <div className="metrics-grid" style={{ marginBottom: '20px' }}>
        <div className="metric-card">
          <div className="metric-label">Total properties</div>
          <div className="metric-value blue">{properties.length}</div>
          <div className="metric-sub">in portfolio</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Mapped</div>
          <div className="metric-value green">{markers.length}</div>
          <div className="metric-sub">with coordinates</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Active jobs</div>
          <div className="metric-value red">{totalActive}</div>
          <div className="metric-sub">across properties</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Contacts linked</div>
          <div className="metric-value amber">{contacts.length}</div>
          <div className="metric-sub">with property</div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '14px', flexWrap: 'wrap', alignItems: 'center' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search properties, companies…" className="field-input" style={{ width: '220px' }} />
        <select value={filterEntity} onChange={e => setFilterEntity(e.target.value)} className="field-select" style={{ width: '190px' }}>
          <option value="">All entities</option>
          {Object.entries(ENTITY_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={filterState} onChange={e => setFilterState(e.target.value)} className="field-select" style={{ width: '100px' }}>
          <option value="">All states</option>
          {allStates.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <div style={{ marginLeft: 'auto', fontSize: '12px', color: '#94A3B8' }}>
          {filtered.length} of {properties.length} properties
        </div>
      </div>

      {/* Entity filter pills */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '14px' }}>
        {Object.entries(ENTITY_COLORS).map(([e, c]) => {
          const count = properties.filter(p => p.assigned_entity === e).length;
          const active = filterEntity === e;
          return (
            <button key={e} onClick={() => setFilterEntity(active ? '' : e)}
              style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', color: active ? c : '#475569', background: active ? c + '12' : 'transparent', border: `1px solid ${active ? c : '#E2E8F0'}`, borderRadius: '20px', padding: '3px 10px', cursor: 'pointer', fontWeight: active ? '600' : '400', transition: 'all 0.15s' }}>
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: c }} />
              {ENTITY_LABELS[e]}
              <span style={{ color: '#94A3B8', fontWeight: '400' }}>({count})</span>
            </button>
          );
        })}
      </div>

      {/* Map */}
      <div style={{ borderRadius: '10px', overflow: 'hidden', border: '1px solid #E2E8F0', marginBottom: '24px', height: '440px' }}>
        {loading ? (
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F8FAFC', fontSize: '13px', color: '#94A3B8' }}>Loading…</div>
        ) : (
          <MapContainer center={[28, -83]} zoom={7} style={{ height: '100%', width: '100%' }} scrollWheelZoom={true}>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <MapFitter markers={filteredMarkers} />
            {filteredMarkers.map(p => {
              const jobs = activeJobs(p.id);
              const pContacts = propContacts(p.id);
              const color = ENTITY_COLORS[p.assigned_entity] || '#94A3B8';
              return (
                <Marker key={p.id} position={[p.lat, p.lng]} icon={makePin(color)}>
                  <Popup maxWidth={260}>
                    <div style={{ fontFamily: 'system-ui', fontSize: '13px' }}>
                      <div style={{ fontWeight: '700', fontSize: '14px', marginBottom: '2px', color: '#0F172A' }}>{p.name}</div>
                      {p.companies?.name && <div style={{ fontSize: '12px', color: '#64748B', marginBottom: '3px' }}>{p.companies.name}</div>}
                      <div style={{ fontSize: '12px', color: '#94A3B8', marginBottom: '8px' }}>
                        {[p.site_address, p.city, p.state].filter(Boolean).join(', ')}
                      </div>
                      {p.assigned_entity && (
                        <span style={{ fontSize: '11px', background: color + '18', color, padding: '2px 8px', borderRadius: '20px', fontWeight: '600', display: 'inline-block', marginBottom: '8px' }}>
                          {ENTITY_LABELS[p.assigned_entity]}
                        </span>
                      )}
                      {jobs > 0 && (
                        <div style={{ fontSize: '12px', color: '#EF4444', fontWeight: '600', marginBottom: '8px' }}>
                          {jobs} active job{jobs > 1 ? 's' : ''}
                        </div>
                      )}
                      {pContacts.length > 0 && (
                        <div style={{ borderTop: '1px solid #F1F5F9', paddingTop: '8px', marginTop: '4px' }}>
                          <div style={{ fontSize: '10px', fontWeight: '600', color: '#94A3B8', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Contacts</div>
                          {pContacts.slice(0, 3).map(c => (
                            <div key={c.id} style={{ marginBottom: '6px' }}>
                              <div style={{ fontWeight: '600', fontSize: '12px', color: '#0F172A' }}>{c.full_name}{c.title ? ` · ${c.title}` : ''}</div>
                              {c.email && <a href={`mailto:${c.email}`} style={{ fontSize: '11px', color: '#3B82F6', textDecoration: 'none', display: 'block' }}>{c.email}</a>}
                              {c.phone && <div style={{ fontSize: '11px', color: '#64748B' }}>{c.phone}</div>}
                            </div>
                          ))}
                          {pContacts.length > 3 && <div style={{ fontSize: '11px', color: '#94A3B8' }}>+{pContacts.length - 3} more</div>}
                        </div>
                      )}
                      <div style={{ borderTop: '1px solid #F1F5F9', paddingTop: '8px', marginTop: '8px' }}>
                        <button onClick={() => navigate('/contacts')} style={{ fontSize: '12px', color: '#3B82F6', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: '500' }}>
                          View all contacts →
                        </button>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>
        )}
      </div>

      {/* List grouped by entity */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {Object.entries(grouped).length === 0 && !loading && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#94A3B8', fontSize: '13px' }}>No properties found.</div>
        )}
        {Object.entries(grouped).map(([entity, props]) => {
          const color = ENTITY_COLORS[entity] || '#94A3B8';
          const label = ENTITY_LABELS[entity] || entity;
          const isOpen = expandedEntity === entity || expandedEntity === null;
          return (
            <div key={entity} style={{ border: '1px solid #E2E8F0', borderRadius: '10px', overflow: 'hidden' }}>
              <div onClick={() => setExpandedEntity(expandedEntity === entity ? null : entity)}
                style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '11px 16px', background: '#F8FAFC', cursor: 'pointer', borderBottom: isOpen ? '1px solid #E2E8F0' : 'none', userSelect: 'none' }}>
                <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: color, flexShrink: 0 }} />
                <span style={{ fontWeight: '700', fontSize: '13px', color: '#0F172A', flex: 1 }}>{label}</span>
                <span style={{ fontSize: '12px', color: '#94A3B8' }}>{props.length} propert{props.length === 1 ? 'y' : 'ies'}</span>
                <span style={{ fontSize: '11px', color: '#CBD5E1', marginLeft: '6px' }}>{isOpen ? '▲' : '▼'}</span>
              </div>
              {isOpen && (
                <div style={{ display: 'grid', gridTemplateColumns: '3fr 2fr 1fr 1fr 70px', gap: '12px', padding: '7px 16px', background: '#FAFAFA', borderBottom: '1px solid #F1F5F9', fontSize: '11px', fontWeight: '600', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  <div>Property</div>
                  <div>Company</div>
                  <div>Contacts</div>
                  <div>Jobs</div>
                  <div></div>
                </div>
              )}
              {isOpen && props.map((p, i) => {
                const jobs = activeJobs(p.id);
                const pContacts = propContacts(p.id);
                const isMapped = markers.find(m => m.id === p.id);
                return (
                  <div key={p.id} style={{
                    display: 'grid', gridTemplateColumns: '3fr 2fr 1fr 1fr 70px',
                    alignItems: 'center', gap: '12px', padding: '10px 16px',
                    borderBottom: i < props.length - 1 ? '1px solid #F8FAFC' : 'none',
                    background: '#fff', fontSize: '13px',
                  }}>
                    <div>
                      <div style={{ fontWeight: '600', color: '#0F172A', marginBottom: '1px' }}>{p.name}</div>
                      <div style={{ fontSize: '11px', color: '#94A3B8' }}>
                        {[p.site_address, p.city, p.state].filter(Boolean).join(', ') || '—'}
                      </div>
                    </div>
                    <div style={{ color: '#475569', fontSize: '12px' }}>{p.companies?.name || '—'}</div>
                    <div>
                      {pContacts.length > 0 ? (
                        <button onClick={() => navigate('/contacts')} style={{ fontSize: '11px', color: '#3B82F6', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: '500' }}>
                          {pContacts.length} contact{pContacts.length > 1 ? 's' : ''} →
                        </button>
                      ) : (
                        <span style={{ fontSize: '11px', color: '#CBD5E1' }}>—</span>
                      )}
                    </div>
                    <div>
                      {jobs > 0 ? (
                        <span style={{ fontSize: '11px', background: '#FEF2F2', color: '#EF4444', padding: '2px 8px', borderRadius: '20px', fontWeight: '600' }}>
                          {jobs} active
                        </span>
                      ) : (
                        <span style={{ fontSize: '11px', color: '#CBD5E1' }}>—</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                      {p.qbo_sub_customer_id && (
                        <span style={{ fontSize: '10px', background: '#F0FDF4', color: '#16A34A', padding: '2px 5px', borderRadius: '4px', fontWeight: '600' }}>QBO</span>
                      )}
                      {isMapped && (
                        <span style={{ fontSize: '10px', color: '#94A3B8' }} title="Mapped">📍</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}